import { useEffect, useRef, useState } from "react";
import { getLastedCurrentPromotionProducts } from "api/Promotion";
import { API_URL } from "env";
import { addItemToCart } from "api/Cart";

/* =========================================================
   TYPES
========================================================= */

type Product = {
    id: number | string
    name: string
    image: string
    price: number
    promoPrice?: number
    discount?: string
    unit?: string
    slug?: string
}

type Voucher = {
    code: string
    title: string
    description: string
    logo?: string
    percent?: number
}

type ProductWithVoucherData = {
    product: Product
    voucher: Voucher
}

/* =========================================================
   BLOCK VOUCHER PRODUCT
========================================================= */

export default function BlockVoucherProduct() {
    const [list, setList] = useState<ProductWithVoucherData[]>([])
    
    useEffect(() => {
        getLastedCurrentPromotionProducts().then(res => {
            if (res.status && res.data?.items.length > 0) {
            const mapped: ProductWithVoucherData[] = [];
            
            for (const promotion of res.data.items) {
                if (mapped.length >= 9) break;

                for (const pp of promotion.promotion_product) {
                if (mapped.length >= 9) break;

                const item = pp.product;
                if (!item) continue;

                const discountPercent = pp.percent || 0;
                const finalPrice = Math.round(item.price * (1 - discountPercent / 100));

                mapped.push({
                    product: {
                    id: item.id,
                    name: item.name,
                    image: item.avatar ? (item.avatar.startsWith('http') ? item.avatar : `${API_URL}/${item.avatar}`) : "",
                    price: finalPrice,
                    promoPrice: discountPercent > 0 ? item.price : undefined,
                    discount: discountPercent > 0 ? `-${discountPercent}%` : undefined,
                    unit: item.short_content || "Hộp",
                    slug: item.slug
                    },
                    voucher: {
                    code: promotion.slug,
                    title: promotion.name,
                    description: promotion.description || "Ưu đãi đặc biệt từ chương trình",
                    percent: pp.percent,
                    }
                });
                }
            }
            setList(mapped);
            }
        }).catch(err => console.error(err));
    }, []);

    return (
        <div id="BLOCK_VOUCHER_PRODUCT">
            <div
                event-block="BLOCK_VOUCHER_PRODUCT"
                className="flex flex-wrap gap-4 rounded-[10px]"
            >
                <Header />

                <div className="flex flex-col gap-4 w-full">
                    <div className="grid xl:grid-cols-12 sm:grid-cols-8 grid-cols-4 gap-4">
                        {list.map(item => (
                            <ProductWithVoucher
                                key={item.product.id}
                                data={item}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* =========================================================
   HEADER
========================================================= */

function Header() {
    return (
        <div
            className="w-full h-[64px] flex items-center justify-center rounded md:bg-white !bg-[#005c29] !bg-no-repeat ![background-size:100%] !bg-cover"
            style={{ background: `url("https://thuocsi.vn/images/bg-header-1.png")` }}
        >
            <div className="md:text-[32px] text-[20px] font-extrabold text-white">
                Mã giảm giá theo sản phẩm
            </div>
        </div>
    );
}

/* =========================================================
   PRODUCT + VOUCHER WRAPPER
========================================================= */

function ProductWithVoucher({ data }: { data: ProductWithVoucherData }) {
    return (
        <div
            className="bg-white rounded-[10px] col-span-4 !bg-no-repeat !bg-cover"
            style={{
                background:
                    "linear-gradient(90deg, rgb(248, 238, 234) 13%, rgb(236, 197, 182) 86%, rgba(248, 132, 86, 0.443) 100%)",
                ["--column" as any]: 2,
                ["--product-column" as any]: 1,
                ["--container-width" as any]: "383px",
            }}
        >
            <div className="flex items-center gap-2 min-h-[240px] h-full">
                <ProductSlider product={data.product} />
                <VoucherInfo voucher={data.voucher} />
            </div>
        </div>
    )
}


function ProductSlider({ product }: { product: Product }) {
    const sliderRef = useRef<HTMLDivElement>(null);

    return (
        <div className="w-[calc(var(--product-column)*180px+(12px*(var(--product-column)+1)))] h-full">
            <div className="relative h-full">
                <div className="overflow-x-auto flex gap-3 p-3 snap-x h-full">
                    <ProductCard product={product} />
                </div>
            </div>
        </div>
    );
}

/* =========================================================
   PRODUCT CARD
========================================================= */

function ProductCard({ product }: { product: Product }) {
    return (
        <div
            id={product.id.toString()}
            className="styles_container__IDdis !border-[1px] !border-[#D9D9D9] min-w-[180px] !max-w-[180px] !h-auto min-h-[220px] hover:!border-[var(--primary-color)] animate-fade snap-start !bg-white
            [&>[class*='underContainer']]:!h-auto
            [&>[class*='underContainer']>[class*='wrapperTooltip']]:hidden
            [&>[class*='underContainer']_[class*='pricePromo']]:hidden
            [&>[class*='underContainer']_[class*='dateExpired']]:hidden
            [&>[class*='underContainer']>[class*='progressContainer']]:hidden
            [&>[class*='underContainer']>[class*='titleContainer']]:!h-auto
            [&>[class*='underContainer']>[class*='titleContainer']>[class*='titleWrapper']]:text-[12px]
            [&>[class*='underContainer']_[class*='countPerItem']]:hidden
            [&>[class*='tooltipRight']]:hidden
            highProfitTribleLayer"
            role="button"
            tabIndex={0}
        // style={{
        //     ["--high-profit-head-color" as any]: "#f5a447",
        //     ["--high-profit-middle-color" as any]: "#f5a447",
        //     ["--high-profit-end-color" as any]: "#ffffff",
        //     ["--high-profit-angle" as any]: "180deg",
        //     ["--high-profit-head-percent-color" as any]: "0%",
        //     ["--high-profit-middle-percent-color" as any]: "50%",
        //     ["--high-profit-end-percent-color" as any]: "100%",
        // }}
        >
            {/* Discount */}
            {product.discount && (
                <p className="MuiTypography-root styles_promo__losWR MuiTypography-body1">{product.discount}</p>
            )}

            {/* Favorite icon */}
            <div className="styles_tooltipRight__KgBN7" role="button">
                <div
                    className="styles_customButton__HNiJK"
                    title="Đánh dấu sản phẩm quan tâm"
                >
                    <svg
                        className="MuiSvgIcon-root styles_icon__Zwjan"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ fontSize: 24, color: "rgba(0,0,0,0.2)" }}
                    >
                        <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" />
                    </svg>
                </div>
            </div>

            {/* Image */}
            <div className="styles_topContainer__8eSB4 !h-[120px] [&>a]:!h-[120px] [&>a]:!w-[120px] [&_[class*='listTag']]:hidden">
                <a
                    className="styles_mobile_rootBase__8z7PQ styles_stylesImage__YoHx0"
                    href={`/product/${product.slug}`}
                >
                    <img
                        src={product.image}
                        alt={product.name}
                        className="styles_image__Y8Jva"
                        style={{ objectFit: "contain" }}
                    />
                </a>
            </div>

            {/* Content */}
            <div className="styles_underContainer__mL2nM">
                <div className="styles_priceContainer__F_qQS px-[8px]">
                    {/* PRICE */}
                    <div className="styles_price__P5IMX" style={{ transform: 'translateX(0px)' }}>
                        <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                            <div
                                className="styles_canvasPrice__vw932"
                                style={{ height: 25 }}
                            >
                                {product.price.toLocaleString()}₫
                            </div>
                        </div>
                    </div>

                    {/* PROMO PRICE */}
                    {product.promoPrice && (
                        <div className="styles_pricePromo__JxTg_">
                            <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                <div
                                    className="styles_canvasPrice__vw932"
                                    style={{ height: 15 }}
                                >
                                    {product.promoPrice.toLocaleString()}₫
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="styles_titleContainer__WYihl">
                    <a
                        className="styles_titleWrapper__FvOnI !text-[12px]"
                        href={`/product/${product.slug}`}
                    >
                        <span>
                            {product.name}
                        </span>
                    </a>
                    <p className="MuiTypography-root styles_countPerItem__OvDsy MuiTypography-body1">
                        {product.unit}
                    </p>
                </div>
            </div>
            <QuantityControl id={product.id} />
        </div>
    );
}

/* =========================================================
   QUANTITY CONTROL (CÓ LOGIC)
========================================================= */

function QuantityControl({ id }: { id: number | string }) {
    const [qty, setQty] = useState(0);

    const updateQty = (delta: number) => {
        const newQty = Math.max(0, qty + delta);
        if (newQty !== qty) {
            addItemToCart(String(id), delta);
            setQty(newQty);
        }
    }

    return (
        <div className="styles_maxWidth__zBhsQ highProfitInputQuantity">
            <div className="styles_container__zH79T styles_quantityControlContainer__djBwR qty-control-lite-wrapper">
                <button
                    type="button"
                    className="styles_minusContainerNew__RSUQE qty-control-lite-minus"
                    onClick={() => updateQty(-1)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M7 12H17" stroke="#005c29" stroke-width="2" stroke-linecap="round"></path></svg>
                </button>

                <div className="styles_groupInput__3rE3S qty-control-lite-input-group">
                    {/* Cart icon + current value */}
                    <div className="styles_cartIconContainer__j1a05">
                        <div className="styles_inputVisible__4CFxn">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={18}
                                height={18}
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M14.3608 3.9375H4.00136L3.11622 2.35095C3.01687 2.17287 2.82892 2.0625 2.625 2.0625H1.5C1.18934 2.0625 0.9375 2.31434 0.9375 2.625C0.9375 2.93566 1.18934 3.1875 1.5 3.1875H2.2947L3.16822 4.75324L5.18278 9.21066L5.18484 9.21521L5.36228 9.60781L3.33965 11.7653C3.19422 11.9204 3.14869 12.1443 3.22199 12.3439C3.29529 12.5435 3.47488 12.6847 3.68614 12.7089L5.52974 12.9196C7.83575 13.1831 10.1643 13.1831 12.4703 12.9196L14.3139 12.7089C14.6225 12.6736 14.8442 12.3948 14.8089 12.0861C14.7736 11.7775 14.4948 11.5559 14.1861 11.5911L12.3425 11.8018C10.1214 12.0557 7.87861 12.0557 5.65749 11.8018L4.92585 11.7182L6.41038 10.1347C6.42474 10.1194 6.43808 10.1035 6.45039 10.0871L7.015 10.1605C7.8062 10.2635 8.60596 10.2837 9.40134 10.2208C11.2564 10.074 12.9758 9.19131 14.1761 7.7693L14.6097 7.25569C14.6242 7.23844 14.6377 7.22033 14.6501 7.20146L15.4581 5.97007C16.0309 5.09724 15.4048 3.9375 14.3608 3.9375Z"
                                    fill="#999999"
                                />
                                <path
                                    d="M4.875 13.875C4.25368 13.875 3.75 14.3787 3.75 15C3.75 15.6213 4.25368 16.125 4.875 16.125C5.49632 16.125 6 15.6213 6 15C6 14.3787 5.49632 13.875 4.875 13.875Z"
                                    fill="#999999"
                                />
                                <path
                                    d="M12 15C12 14.3787 12.5037 13.875 13.125 13.875C13.7463 13.875 14.25 14.3787 14.25 15C14.25 15.6213 13.7463 16.125 13.125 16.125C12.5037 16.125 12 15.6213 12 15Z"
                                    fill="#999999"
                                />
                            </svg>
                        </div>

                        <span className="styles_currentValue__ZYyd_">{qty}</span>
                    </div>

                    {/* Input number */}
                    <div className="MuiInput-root MuiInputBase-root MuiInputBase-colorPrimary styles_newCardInput__AYuYU qty-control-lite-input css-1f63zq5">
                        <input
                            type="number"
                            className="MuiInput-input MuiInputBase-input css-mnn31"
                            defaultValue={1}
                            value={qty} readOnly
                        />
                    </div>
                </div>

                <button
                    type="button"
                    className="styles_addContainerNew__GECz2 qty-control-lite-plus"
                    onClick={() => updateQty(1)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 17V7" stroke="#005c29" stroke-width="2" stroke-linecap="round"></path><path d="M7 12L17 12" stroke="#005c29" stroke-width="2" stroke-linecap="round"></path></svg>
                </button>
            </div>
        </div>
    );
}

/* =========================================================
   VOUCHER INFO
========================================================= */

function VoucherInfo({ voucher }: { voucher: Voucher }) {
    return (
        <div className="flex flex-1 h-full min-h-[240px] py-3">
            <div
                track-voucher-item=""
                data-code={voucher.code}
                className="
                    h-full w-full min-h-[240px]
                    flex flex-col gap-1
                    justify-between px-4
                    animate-fade
                "
            >
                <h3 className="font-bold text-[16px] text-center">{voucher.title}</h3>
                <div className="h-[50px] relative">
                    {voucher.percent !== undefined && (
                        <div
                            className="
                                absolute
                                w-[80px] h-[80px]
                                rounded-full
                                bg-[var(--primary-color)]
                                text-white
                                flex flex-col items-center justify-center
                                font-bold text-[20px]
                            "
                            style={{ top: '-20px', left: '50%', transform: 'translateX(-50%)' }}
                        >
                            <span>GIẢM</span>
                            <span>{voucher.percent}%</span>
                            
                        </div>
                    )}  
                </div>

                <p className="text-[12px] line-clamp-3 text-center">
                    {voucher.description}
                </p>

                <button
                    className="
                        border-[1px] border-[var(--primary-color)]
                        px-3 py-1.5
                        rounded-[30px]
                        text-[var(--primary-color)]
                        text-[12px]
                        mx-auto
                    "
                >
                    Xem điều kiện
                </button>
            </div>
        </div>
    );
}
